import * as React from 'react';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Panel from '../panel/Panel';
import { useStores } from '../../stores';
import { Button } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useTranslation } from "react-i18next";
import { FieldServiceGroup } from '../../domain/FieldServiceGroup';
import { useOpenDialog } from '../../hooks/useOpenDialog';
import GroupUpdateDialog from './GroupUpdateDialog';
import GroupCreateDialog from './GroupCreateDialog';
import Can, { AbilityContext } from '../../components/Can';
import { useNavigate } from 'react-router-dom';
import FieldServiceGroupTable from './table/FieldServiceGroupTable';
import { Publisher } from '../../domain/Publisher';
import { useConfirm } from 'material-ui-confirm';

export default observer(() => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { publisherStore } = useStores();
    const ability = React.useContext(AbilityContext);
    const confirm = useConfirm();
    const { isOpen, openDialog, closeDialog } = useOpenDialog();
    const [selectedGroup, setSelectedGroup] = React.useState<FieldServiceGroup>();

    React.useEffect(() => {
        if (!publisherStore.groups) {
            publisherStore.getAllServiceGroups();
        }
        if (!publisherStore.publishers.length) {
            publisherStore.getAllPublishers();
        }
    }, []);

    const goToPublisher = (publisher: Publisher) => {
        if (ability.can("update", "Publisher")) {
            navigate(`/publishers/${publisher.id}`)
        }
    }

    const goToReports = (group: FieldServiceGroup) => {
        navigate(`/group-reports/${group.id}`)
    }

    const open = (group: FieldServiceGroup) => {
        setSelectedGroup(group);
        openDialog("update-group")
    }

    const handleDelete = (id: number) => {
        confirm({
            title: t("are_you_sure"),
            description: `${t("this_will_permanently_delete")} ${selectedGroup!.name}`,
            confirmationText: t("yes"),
            cancellationText: t("no")
        }).then(() => {
            publisherStore.deleteServiceGroup(id);
            setSelectedGroup(undefined);
            closeDialog();
        });
    }

    const handleClose = () => {
        setSelectedGroup(undefined);
        closeDialog();
    }

    return (
        <Panel
            title={t("groups")}
            headerItems={[
                <Can key="new_group_btn" do="create" on='FieldServiceGroup'>
                    <Button
                        type="button"
                        color="inherit"
                        onClick={() => openDialog("create-group")}>
                        {t("create_new_group")}
                    </Button>
                </Can>
            ]}>
            <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <FieldServiceGroupTable
                        groups={publisherStore.groups}
                        publishers={publisherStore.publishers}
                        onGroupEdit={open}
                        onPublisherEdit={goToPublisher}
                        onGoToReports={goToReports}
                    />
                </Paper>
            </Container>
            <GroupCreateDialog isOpen={isOpen("create-group")} onClose={handleClose} />
            <GroupUpdateDialog
                isOpen={isOpen("update-group")}
                group={selectedGroup}
                onDelete={handleDelete}
                onClose={handleClose}
            />
        </Panel >
    );
});